import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { shopsApi } from '@/services/api';
import { Product } from '@/types';
import { ROUTES } from '@/utils/constants';
import { getPrimaryImage, getImageUrl, getPlaceholderImage } from '@/utils/imageUtils';
import Button from '@/components/ui/Button';
import ProductCard from '@/components/ui/ProductCard';
import {
  ArrowLeftIcon,
  ShareIcon,
  BuildingStorefrontIcon,
  StarIcon,
  ShoppingBagIcon,
  CheckBadgeIcon,
  MapPinIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';

interface ShopData {
  id: number;
  name: string;
  description: string;
  slug: string;
  shop_url: string;
  logo_url: string | null;
  banner_url: string | null;
  products: Product[];
  total_products: number;
  admin_rating: number;
  visibility_score: number;
  // Additional business info fields
  business_email?: string;
  business_phone?: string;
  business_address?: string;
  business_website?: string;
  business_hours?: string;
}

const PublicShopPage: React.FC = () => {
  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const navigate = useNavigate();

  const [shop, setShop] = useState<ShopData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState<Product[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [activeTab, setActiveTab] = useState<'products' | 'about'>('products');
  const productsPerPage = 20;

  useEffect(() => {
    const fetchShop = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let response;
        if (slug) {
          response = await shopsApi.getPublicBySlug(slug, {
            skip: 0,
            limit: productsPerPage
          });
        } else if (id) {
          response = await shopsApi.getPublicById(Number(id), {
            skip: 0,
            limit: productsPerPage
          });
        } else {
          setError('Invalid shop identifier');
          setIsLoading(false);
          return;
        }

        if (response.data) {
          setShop(response.data);
          setProducts(response.data.products || []);
          setTotalProducts(response.data.total_products || 0);
        }
      } catch (err: any) {
        console.error('Error fetching shop:', err);
        setError(err.response?.data?.detail || 'Shop not found');
      } finally {
        setIsLoading(false);
      }
    };

    fetchShop();
  }, [slug, id]);

  const handleLoadMore = async () => {
    if (!shop) return;

    try {
      const skip = currentPage * productsPerPage;
      let response;

      if (shop.slug) {
        response = await shopsApi.getProductsBySlug(shop.slug, {
          skip,
          limit: productsPerPage
        });
      } else {
        response = await shopsApi.getProductsByShopId(shop.id, {
          skip,
          limit: productsPerPage
        });
      }

      if (response.data) {
        setProducts(prev => [...prev, ...response.data]);
        setCurrentPage(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error loading more products:', err);
      toast.error('Failed to load more products');
    }
  };

  const handleShare = async () => {
    if (!shop) return;

    const url = shop.shop_url || window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shop.name,
          text: shop.description || `Check out ${shop.name}`,
          url: url,
        });
      } catch (err) {
        // User cancelled or error occurred
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Shop link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const handleProductClick = (productId: number) => {
    navigate(`${ROUTES.PRODUCTS}/${productId}`);
  };

  // Render star rating
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <StarSolidIcon key={`full-${i}`} className="h-5 w-5 text-yellow-400" />
        ))}
        {hasHalfStar && (
          <div className="relative h-5 w-5">
            <StarIcon className="absolute h-5 w-5 text-yellow-400" />
            <div className="absolute overflow-hidden h-5 w-2.5">
              <StarSolidIcon className="h-5 w-5 text-yellow-400" />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <StarIcon key={`empty-${i}`} className="h-5 w-5 text-gray-300" />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600 font-medium">Loading shop...</p>
        </div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <BuildingStorefrontIcon className="h-20 w-20 text-gray-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Shop Not Found</h2>
          <p className="text-gray-600 mb-8 text-lg">{error || 'The shop you are looking for does not exist.'}</p>
          <Button onClick={() => navigate(ROUTES.HOME)} className="px-8 py-3">
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const hasMoreProducts = products.length < totalProducts;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Hero Section with Banner and Logo Overlay */}
      <div className="relative">
        {/* Banner */}
        <div className="relative h-72 md:h-96 bg-gradient-to-r from-primary-500 to-primary-700 overflow-hidden">
          {shop.banner_url ? (
            <>
              <img
                src={getImageUrl(shop.banner_url)}
                alt={shop.name}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px'
                }}></div>
              </div>
            </div>
          )}
        </div>

        {/* Shop Info Overlay */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative -mt-32 pb-8">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                  {/* Logo */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    {shop.logo_url ? (
                      <div className="relative h-32 w-32 md:h-40 md:w-40 rounded-2xl overflow-hidden border-4 border-white shadow-xl ring-4 ring-gray-100">
                        <img
                          src={getImageUrl(shop.logo_url)}
                          alt={shop.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = getPlaceholderImage(400, 400, shop.name.charAt(0));
                          }}
                        />
                      </div>
                    ) : (
                      <div className="h-32 w-32 md:h-40 md:w-40 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center border-4 border-white shadow-xl ring-4 ring-gray-100">
                        <span className="text-5xl md:text-6xl font-bold text-white">
                          {shop.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </motion.div>

                  {/* Shop Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 truncate">
                            {shop.name}
                          </h1>
                          {shop.admin_rating >= 4.0 && (
                            <CheckBadgeIcon className="h-8 w-8 text-primary-500 flex-shrink-0" title="Verified Shop" />
                          )}
                        </div>

                        {shop.description && (
                          <p className="text-gray-600 text-lg mb-4 line-clamp-2">
                            {shop.description}
                          </p>
                        )}

                        {/* Stats Bar */}
                        <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm">
                          {shop.admin_rating > 0 && (
                            <div className="flex items-center gap-2">
                              {renderStars(shop.admin_rating)}
                              <span className="text-lg font-bold text-gray-900">{shop.admin_rating.toFixed(1)}</span>
                              <span className="text-gray-500">rating</span>
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-gray-700">
                            <ShoppingBagIcon className="h-5 w-5 text-primary-600" />
                            <span className="font-semibold">{totalProducts}</span>
                            <span className="text-gray-500">{totalProducts === 1 ? 'Product' : 'Products'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Share Button */}
                      <Button
                        variant="outline"
                        onClick={handleShare}
                        className="flex-shrink-0 gap-2 hover:scale-105 transition-transform"
                      >
                        <ShareIcon className="h-5 w-5" />
                        <span className="hidden md:inline">Share</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-t border-gray-200 bg-gray-50">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('products')}
                    className={cn(
                      'flex-1 px-6 py-4 text-sm font-semibold transition-all',
                      activeTab === 'products'
                        ? 'text-primary-600 border-b-2 border-primary-600 bg-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    )}
                  >
                    Products ({totalProducts})
                  </button>
                  <button
                    onClick={() => setActiveTab('about')}
                    className={cn(
                      'flex-1 px-6 py-4 text-sm font-semibold transition-all',
                      activeTab === 'about'
                        ? 'text-primary-600 border-b-2 border-primary-600 bg-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    )}
                  >
                    About
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {products.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
                <ShoppingBagIcon className="h-20 w-20 text-gray-300 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No Products Yet</h3>
                <p className="text-gray-600 text-lg max-w-md mx-auto">
                  This shop hasn't added any products yet. Check back soon!
                </p>
              </div>
            ) : (
              <>
                {/* Products Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
                  {products.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <ProductCard
                        product={product}
                        onProductClick={() => handleProductClick(product.id)}
                        className="h-full hover:shadow-xl transition-all duration-300"
                      />
                    </motion.div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMoreProducts && (
                  <div className="text-center">
                    <Button
                      variant="outline"
                      onClick={handleLoadMore}
                      className="px-10 py-3 text-base font-semibold hover:scale-105 transition-transform"
                    >
                      Load More Products
                    </Button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* About Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <BuildingStorefrontIcon className="h-7 w-7 text-primary-600 mr-3" />
                  About {shop.name}
                </h2>

                {shop.description ? (
                  <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                    {shop.description}
                  </p>
                ) : (
                  <p className="text-gray-500 italic">No description available.</p>
                )}

                {/* Trust Badges */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Shop With Us</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <CheckBadgeIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Verified Seller</p>
                        <p className="text-sm text-gray-600">Trusted by our platform</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <StarSolidIcon className="h-6 w-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Quality Products</p>
                        <p className="text-sm text-gray-600">Curated selection</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <ShoppingBagIcon className="h-6 w-6 text-primary-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Fast Shipping</p>
                        <p className="text-sm text-gray-600">Quick delivery options</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <ClockIcon className="h-6 w-6 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Responsive Support</p>
                        <p className="text-sm text-gray-600">Here to help you</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  {shop.business_email && (
                    <div className="flex items-start gap-3">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <a href={`mailto:${shop.business_email}`} className="text-primary-600 hover:text-primary-700 font-medium">
                          {shop.business_email}
                        </a>
                      </div>
                    </div>
                  )}

                  {shop.business_phone && (
                    <div className="flex items-start gap-3">
                      <PhoneIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Phone</p>
                        <a href={`tel:${shop.business_phone}`} className="text-primary-600 hover:text-primary-700 font-medium">
                          {shop.business_phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {shop.business_address && (
                    <div className="flex items-start gap-3">
                      <MapPinIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Address</p>
                        <p className="text-gray-700">{shop.business_address}</p>
                      </div>
                    </div>
                  )}

                  {shop.business_website && (
                    <div className="flex items-start gap-3">
                      <GlobeAltIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Website</p>
                        <a
                          href={shop.business_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 font-medium break-all"
                        >
                          {shop.business_website}
                        </a>
                      </div>
                    </div>
                  )}

                  {shop.business_hours && (
                    <div className="flex items-start gap-3">
                      <ClockIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Business Hours</p>
                        <p className="text-gray-700 whitespace-pre-line">{shop.business_hours}</p>
                      </div>
                    </div>
                  )}

                  {!shop.business_email && !shop.business_phone && !shop.business_address &&
                   !shop.business_website && !shop.business_hours && (
                    <p className="text-gray-500 text-sm italic">No contact information available.</p>
                  )}
                </div>
              </div>

              {/* Shop Stats */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl shadow-sm border border-primary-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Shop Statistics</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Total Products</span>
                    <span className="text-2xl font-bold text-primary-700">{totalProducts}</span>
                  </div>
                  {shop.admin_rating > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Shop Rating</span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-primary-700">{shop.admin_rating.toFixed(1)}</span>
                        <StarSolidIcon className="h-6 w-6 text-yellow-400" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PublicShopPage;
